/**
 * Route builders for the Brands & Vendors area.
 *
 * The readiness chips and task list navigate to "the surface that fixes it",
 * so those targets are named once here rather than spelled out at each call site.
 */

import type { ChipKey, TaskAction } from './brandLifecycle';

export const BV_ROOT = '/app/admin/brands-vendors';

export const bvRoutes = {
  directory: BV_ROOT,
  logos: (brandId?: string) => (brandId ? `${BV_ROOT}/logos?brand=${encodeURIComponent(brandId)}` : `${BV_ROOT}/logos`),
  organizations: (orgId?: string) =>
    orgId ? `${BV_ROOT}/organizations?org=${encodeURIComponent(orgId)}` : `${BV_ROOT}/organizations`,
  approvals: (requestId?: string | null) =>
    requestId ? `${BV_ROOT}/approvals?case=${encodeURIComponent(requestId)}` : `${BV_ROOT}/approvals`,
  dealerLists: `${BV_ROOT}/dealer-lists`,
  locatorSources: `${BV_ROOT}/dealer-lists`,
  brand: (brandId: string) => `${BV_ROOT}/brands/${encodeURIComponent(brandId)}`,
  dashboard: (brandId: string) => `${BV_ROOT}/brands/${encodeURIComponent(brandId)}/dashboard`,
  signup: '/vendor-signup',
  /** The data ingestion console, where a source is actually configured. */
  ingestion: (brandId: string) => `/app/admin/data-ingestion?brand=${encodeURIComponent(brandId)}`,
};

/** Where a readiness chip click lands. */
export const chipTarget = (key: ChipKey, brandId: string): string => {
  switch (key) {
    case 'logo':
      return bvRoutes.logos(brandId);
    case 'claim':
      return bvRoutes.organizations();
    default:
      return bvRoutes.brand(brandId);
  }
};

/**
 * Where a brand-page task button lands, or `null` when the task is genuinely
 * handled in place by the page.
 *
 * Only `fix-ingestion` is repaired in place (re-running an existing source is a
 * real remedy for a failed run). Everything else needs a surface that can
 * actually perform the fix: configuring a source, seeding a catalog and
 * inviting a rep are not things this page can do by firing a job.
 */
export const taskTarget = (action: TaskAction, brandId: string): string | null => {
  switch (action.kind) {
    case 'open-logo-library':
    case 'upload-logo':
      return bvRoutes.logos(brandId);
    case 'review-claim':
      return bvRoutes.approvals(action.requestId);
    case 'configure-ingestion':
    case 'see-quality-gaps':
      // Both are the ingestion console's job: one has no source yet, the other
      // has gaps that only re-mapping or re-ingesting can close.
      return bvRoutes.ingestion(brandId);
    case 'invite-rep':
      return bvRoutes.organizations();
    case 'fix-ingestion':
      return null;
    default:
      return null;
  }
};
